/** Every row keeps its own publication gate. Read before retrying an uncertain write. */
import { ApiError } from "@/lib/api/client";
/** The private writer distinguishes a definite refusal from a lost outcome. */
export class PublicationFailure extends Error {
  constructor(public outcome: "refused" | "uncertain", reason: string) { super(reason); }
}
/** Both API serializers use UTC, one with Z and one with +00:00. Keep
 * microseconds: a later save in the same millisecond is still a new version. */
export function samePublicationVersion(left: string, right: string): boolean {
  const canonical = (value: string) => value.replace(/(?:\.(\d+))?(?:Z|\+00:00)$/, (_, fraction: string | undefined) => `.${(fraction ?? "").padEnd(6, "0")}Z`);
  return canonical(left) === canonical(right);
}

export type PublicationCheck = "published" | "draft" | "changed";
export type PublicationResult = "published" | "private" | "failed" | "uncertain" | "changed";
export interface OutgoingRecord {
  id: string;
  title: string;
  content: Array<{ label: string; value: string }>;
  scope: string;
  dependency?: string;
  blockedReason?: string;
  inspect: () => Promise<PublicationCheck>;
  publish: () => Promise<unknown>;
}

export async function publishReviewedRecords(
  records: OutgoingRecord[], selected: ReadonlySet<string>, previous: Record<string, PublicationResult> = {},
  onFailure?: (id: string, message: string) => void,
): Promise<Record<string, PublicationResult>> {
  const results = { ...previous };
  for (const record of records) {
    if (results[record.id] === "published") continue;
    if (results[record.id] === "uncertain") continue;
    if (!selected.has(record.id) || record.blockedReason) { results[record.id] = "private"; continue; }
    // A newer record must be reviewed again, never retried under old consent.
    if (results[record.id] === "changed") continue;
    try {
      const before = await record.inspect();
      if (before !== "draft") { results[record.id] = before; continue; }
    } catch (error) { results[record.id] = "failed"; onFailure?.(record.id, error instanceof ApiError ? error.message : "The record could not be read. Nothing was sent."); continue; }
    try {
      await record.publish();
      const after = await record.inspect();
      results[record.id] = after === "draft" ? "failed" : after;
    } catch (error) {
      // Even a lost response may have committed. The next attempt reads first.
      const refused = error instanceof PublicationFailure ? error.outcome === "refused" : error instanceof ApiError && error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status);
      results[record.id] = refused ? "failed" : "uncertain";
      onFailure?.(record.id, error instanceof ApiError || error instanceof PublicationFailure ? error.message : "The publication outcome could not be confirmed.");
    }
  }
  return results;
}

/** Checking an unknown outcome never sends a second publication request. */
export async function checkReviewedRecords(records: OutgoingRecord[], previous: Record<string, PublicationResult>) {
  const results = { ...previous };
  for (const record of records) {
    if (results[record.id] !== "uncertain") continue;
    try {
      const current = await record.inspect();
      results[record.id] = current === "draft" ? "failed" : current;
    } catch { /* Keep the unconfirmed result until its read succeeds. */ }
  }
  return results;
}

export const PUBLICATION_RESULT: Record<PublicationResult, string> = {
  published: "Published", private: "Stays a draft", failed: "Not published",
  uncertain: "Not confirmed",
  changed: "Changed since review. Keep editing, then review this record again.",
};

export function publicationReceipt(records: OutgoingRecord[], results: Record<string, PublicationResult>, failures: Record<string, string>, retried: string[] = []) {
  const names = (state: PublicationResult) => records.filter(record => results[record.id] === state).map(record => record.title).join(", ");
  const lines = [names("published") && `Published: ${names("published")}.`, names("private") && `${names("private")}: stays a draft.`,
    names("failed") && `Not published: ${names("failed")}. ${records.filter(record => results[record.id] === "failed").map(record => failures[record.id]).filter(Boolean).join(" ")} Your changes are kept.`,
    names("uncertain") && `Not confirmed: ${names("uncertain")}. The request may have landed; check again before sending it again.`,
    names("changed") && `${names("changed")}: changed since review. Review the current record before publishing.`];
  const retryNames = records.filter(record => retried.includes(record.id) && results[record.id] === "published").map(record => record.title);
  if (retryNames.length) lines.push(`Retried only ${retryNames.join(", ")}: published. Nothing else was sent again.`);
  if (records.some(record => record.dependency && results[record.id] === "published") && !records.some(record => record.id.startsWith("plan:") && results[record.id] === "published")) lines.push("Published readings and tasks reach students once the session plan is published.");
  return lines.filter(Boolean).join(" ").replace(/\s{2,}/g, " ");
}
